import { Box, Paper, Stack, TextField } from "@mui/material";
import { PaperStyles, TextFieldStyles } from "../../styles/commonStyles";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import AddressFields from "./addressFields.component";
import SubmitButton from "../buttons/submitButton.component";
import CustomDialog from "../muiDialogs/dialog.component";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  blockUserStart,
  deleteUserStart,
  resetCreateUserState,
  resetUpdateUserState,
} from "../../store/features/user/userSlice";
import { dialogMessages } from "./utils";
import UserActionPanel from "../userActionPanel/userActionPanel.component";
import SuccessDialog from "../muiDialogs/successDialog.component";
import { useIsMobile } from "../../hook/useIsMobile";

const UserForm = ({
  defaultValues = {},
  onSubmit,
  isLoading,
  mode = "create",
  error,
  success,
  user,
  accessToken,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isBlocked = user?.status === "Blocked";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState(null);
  const [isAction, setIsAction] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleDialogConfirm = () => {
    if (dialogMode) {
      if (dialogMode === "block" || dialogMode === "unblock") {
        dispatch(blockUserStart({ userId: user.id, accessToken }));
        setDialogOpen(false);
      } else if (dialogMode === "delete") {
        dispatch(deleteUserStart({ userId: user.id, accessToken }));
        setDialogOpen(false);
      }
    }
  };

  const handleSuccessDialogConfirm = () => {
    dispatch(resetCreateUserState());
    dispatch(resetUpdateUserState());
    setDialogOpen(false);
    navigate("/admin/users");
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setIsAction(false);
  };

  useEffect(() => {
    if (success && (dialogMode === "block" || dialogMode === "unblock")) {
      dispatch(resetCreateUserState());
      dispatch(resetUpdateUserState());
      navigate("/admin/users");
    } else if (success && dialogMode === "delete") {
      dispatch(resetCreateUserState());
      dispatch(resetUpdateUserState());
      navigate("/admin/users");
    }
  }, [success, dispatch, navigate, dialogMode, dialogOpen, mode]);

  useEffect(() => {
    if (success && !dialogMode) {
      setDialogOpen(true);
    }
  }, [success, dialogMode]);

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)} disabled={isLoading}>
        <Paper sx={PaperStyles(isMobile)} elevation={0}>
          {mode === "update" && (
            <UserActionPanel
              user={user}
              onBlock={() => {
                setDialogMode(isBlocked ? "unblock" : "block");
                setIsAction(true);
                setDialogOpen(true);
              }}
              onDelete={() => {
                setDialogMode("delete");
                setIsAction(true);
                setDialogOpen(true);
              }}
              isBlocked={isBlocked}
            />
          )}
          <Box
            display="flex"
            alignItems="center"
            gap="16px"
            justifyContent="center"
            flexDirection={isMobile ? "column" : "row"}
          >
            <TextField
              variant="filled"
              id="company-name-field"
              label="Ragione Sociale"
              fullWidth
              sx={TextFieldStyles}
              {...register("company_name", { required: "Campo obbligatorio" })}
              error={!!errors.company_name || !!error}
              helperText={errors.company_name?.message}
            />
            <TextField
              variant="filled"
              id="email-field"
              label="Indirizzo Email"
              fullWidth
              sx={TextFieldStyles}
              {...register("email", {
                required: "Campo obbligatorio",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Email non valida",
                },
              })}
              error={!!errors.email || !!error}
              helperText={errors.email?.message}
            />
          </Box>

          <Stack spacing={4} mt={4}>
            <AddressFields
              register={register}
              title="Indirizzo Sede Legale"
              fieldPrefix={"legal_address"}
            />
            <AddressFields
              register={register}
              title="Indirizzo Sede Amministrativa"
              fieldPrefix={"administrative_address"}
            />
          </Stack>
        </Paper>
        <SubmitButton
          label={mode === "create" ? "Salva Utente" : "Aggiorna Utente"}
          type="submit"
          disabled={isLoading}
        />
      </form>
      {dialogMode ? (
        <CustomDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          title={dialogMessages.user[dialogMode || mode].title}
          description={dialogMessages.user[dialogMode || mode].description}
          actions={isAction}
        />
      ) : (
        <SuccessDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleSuccessDialogConfirm}
          title={dialogMessages.user[dialogMode || mode].title}
          description={dialogMessages.user[dialogMode || mode].description}
        />
      )}
    </Box>
  );
};

export default UserForm;
